import React, { useEffect, useState, useRef } from "react";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TextField,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Grid,
  Divider,
  TablePagination,
  InputAdornment,
} from "@mui/material";
import { ShoppingBag, Visibility, Edit, Refresh, FilterAltOff } from "@mui/icons-material";
import { apiList, invokeApi, invokeGetApi } from "../../../services/apiServices";

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [newOrderStatus, setNewOrderStatus] = useState("");
  const [newPaymentStatus, setNewPaymentStatus] = useState("");
  const [editCustomerName, setEditCustomerName] = useState("");
  const [editCustomerPhone, setEditCustomerPhone] = useState("");
  const [editCustomerEmail, setEditCustomerEmail] = useState("");
  const [editShippingAddress, setEditShippingAddress] = useState("");
  const [updating, setUpdating] = useState(false);
  const isFetchingRef = useRef(false);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const demoOrders = [
    {
      id: 1,
      order_number: "ORD-2026-1001",
      customer_name: "Rahul Sharma",
      customer_email: "rahul@example.com",
      customer_phone: "9876543210",
      total_amount: 499.00,
      payment_status: "Paid",
      order_status: "Completed",
      shipping_address: "123 Main St, Indiranagar, Bangalore - 560038",
      created_at: "2026-09-30 10:00:00",
      items: [{ product_name: "Windows 10 Pro Retail Key", price: 499.00, quantity: 1, total: 499.00 }]
    },
    {
      id: 2,
      order_number: "ORD-2026-1002",
      customer_name: "Anita Verma",
      customer_email: "anita@example.com",
      customer_phone: "9123456789",
      total_amount: 1499.00,
      payment_status: "Paid",
      order_status: "Processing",
      shipping_address: "45 Park Avenue, Banjara Hills, Hyderabad - 500034",
      created_at: "2026-09-30 11:30:00",
      items: [{ product_name: "MS Office 2024 Home & Business", price: 1499.00, quantity: 1, total: 1499.00 }]
    }
  ];

  const fetchOrders = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    try {
      let apiOrders = [];
      try {
        const response = await invokeGetApi(apiList.getOrders || "/getOrders");
        const list = response?.data?.orders || response?.data?.productOrder;
        if (Array.isArray(list)) {
          apiOrders = list;
        }
      } catch (e) {
        console.warn("Could not fetch orders from API:", e);
      }

      let rawOrdersList = [];
      if (apiOrders.length > 0) {
        // Authoritative source: real data directly from MySQL database
        rawOrdersList = apiOrders;
      } else {
        // Fallback to local storage only if database has no records
        let localDashboardOrders = [];
        try {
          const stored = localStorage.getItem('ar_dashboard_orders');
          if (stored) localDashboardOrders = JSON.parse(stored);
        } catch (e) {}

        let customerOrders = [];
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('ar_customer_orders_')) {
              const data = JSON.parse(localStorage.getItem(key) || '[]');
              if (Array.isArray(data)) customerOrders.push(...data);
            }
          }
        } catch (e) {}

        const localCombined = [...localDashboardOrders, ...customerOrders];
        rawOrdersList = localCombined.length > 0 ? localCombined : [...demoOrders];
      }

      const seen = new Set();
      const uniqueOrders = [];

      for (const ord of rawOrdersList) {
        const num = String(ord.order_number || ord.id || ord.invoice_number || '').trim();
        if (!num || seen.has(num)) continue;
        seen.add(num);

        uniqueOrders.push({
          id: ord.id || num,
          order_number: num,
          customer_name: ord.customer_name || ord.customerName || ord.fullName || 'Customer',
          customer_email: ord.customer_email || ord.customerEmail || ord.email || '',
          customer_phone: ord.customer_phone || ord.customerPhone || ord.phoneNumber || ord.phone || '',
          total_amount: parseFloat(ord.total_amount || ord.totalAmount || ord.total || 0),
          payment_status: ord.payment_status || ord.paymentStatus || 'Paid',
          order_status: ord.order_status || ord.orderStatus || 'Processing',
          shipping_address: ord.shipping_address || ord.address || '',
          created_at: ord.created_at || ord.createdAt || new Date().toISOString(),
          items: ord.items || ord.order_items || [],
        });
      }

      // Sort newest first: primary sort by created_at, secondary sort by id DESC
      uniqueOrders.sort((a, b) => {
        const timeA = new Date(String(a.created_at || '').replace(/-/g, '/')).getTime() || 0;
        const timeB = new Date(String(b.created_at || '').replace(/-/g, '/')).getTime() || 0;
        if (timeB !== timeA) return timeB - timeA;
        return (Number(b.id) || 0) - (Number(a.id) || 0);
      });
      setOrders(uniqueOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders(demoOrders);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    fetchOrders();

    // Only use polling for auto-refresh — no storage/event listeners
    // (those caused cascade loops when the same component dispatched storage events)
    const interval = setInterval(fetchOrders, 30000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleOpenDetail = (order) => {
    setSelectedOrder(order);
    setOpenDetailDialog(true);
  };

  const handleOpenStatus = (order) => {
    setSelectedOrder(order);
    setNewOrderStatus(order.order_status || "Processing");
    setNewPaymentStatus(order.payment_status || "Paid");
    setEditCustomerName(order.customer_name || "");
    setEditCustomerPhone(order.customer_phone || "");
    setEditCustomerEmail(order.customer_email || "");
    setEditShippingAddress(order.shipping_address || "");
    setOpenStatusDialog(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;
    setUpdating(true);
    try {
      const payload = {
        orderId: selectedOrder.id,
        order_id: selectedOrder.id,
        order_number: selectedOrder.order_number,
        status: newOrderStatus,
        order_status: newOrderStatus,
        payment_status: newPaymentStatus,
        paymentStatus: newPaymentStatus,
        customer_name: editCustomerName,
        customer_phone: editCustomerPhone,
        customer_email: editCustomerEmail,
        shipping_address: editShippingAddress,
      };

      try {
        await invokeApi(apiList.updateOrderStatus || "/updateOrderStatus", payload);
      } catch (e) {
        console.warn("API updateOrderStatus warning:", e);
      }

      try {
        await invokeApi(apiList.updateOrder || "/updateOrder", payload);
      } catch (e) {}

      // Sync updated fields to local storage
      const updateInStorage = (key) => {
        try {
          const list = JSON.parse(localStorage.getItem(key) || '[]');
          const updated = list.map(o => {
            const num = String(o.order_number || o.id || o.invoice_number);
            const targetNum = String(selectedOrder.order_number || selectedOrder.id);
            if (num === targetNum) {
              return {
                ...o,
                order_status: newOrderStatus,
                payment_status: newPaymentStatus,
                customer_name: editCustomerName,
                customer_phone: editCustomerPhone,
                customer_email: editCustomerEmail,
                shipping_address: editShippingAddress,
              };
            }
            return o;
          });
          localStorage.setItem(key, JSON.stringify(updated));
        } catch (e) {}
      };

      updateInStorage('ar_dashboard_orders');

      // Update in ALL ar_customer_orders_* keys (scan all to ensure we find the order)
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('ar_customer_orders_')) {
            updateInStorage(key);
          }
        }
      } catch (e) {}

      // Immediately update local state so table updates instantly
      setOrders(prev => prev.map(o => {
        const num = String(o.order_number || o.id || o.invoice_number);
        const targetNum = String(selectedOrder.order_number || selectedOrder.id);
        if (num === targetNum) {
          return {
            ...o,
            order_status: newOrderStatus,
            payment_status: newPaymentStatus,
            customer_name: editCustomerName,
            customer_phone: editCustomerPhone,
            customer_email: editCustomerEmail,
            shipping_address: editShippingAddress,
          };
        }
        return o;
      }));

      setOpenStatusDialog(false);
      // Re-fetch from server to confirm the update
      fetchOrders();
    } catch (error) {
      console.error("Failed to update order status:", error);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusChipColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed": return "success";
      case "processing": return "warning";
      case "pending": return "info";
      case "cancelled": return "error";
      default: return "default";
    }
  };

  // Filter orders by search, status, and date range
  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_phone?.includes(searchQuery) ||
      order.customer_email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "ALL" || order.order_status?.toLowerCase() === statusFilter.toLowerCase();

    // Date range filter — direct YYYY-MM-DD string comparison (100% timezone-safe)
    const matchesDate = (() => {
      if (!startDate && !endDate) return true;
      if (!order.created_at) return true;
      const orderDateStr = String(order.created_at).slice(0, 10);

      if (startDate && orderDateStr < startDate) return false;
      if (endDate && orderDateStr > endDate) return false;
      return true;
    })();

    return matchesSearch && matchesStatus && matchesDate;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("ALL");
    setStartDate("");
    setEndDate("");
    setPage(0);
  };

  const paginatedOrders = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ display: "flex", bgcolor: "#F9FAF4", minHeight: "100vh" }}>
      <Sidenav />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Header />
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <ShoppingBag sx={{ fontSize: 36, color: "#1E40AF" }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#1E40AF" }}>
                Orders Manager
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchOrders}
              sx={{ borderColor: "#1E40AF", color: "#1E40AF" }}
            >
              Refresh Orders
            </Button>
          </Box>

          {/* Search & Filter Bar */}
          <Paper elevation={0} sx={{ p: 2, mb: 3, border: "1px solid #EAEAEA", display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search by Order #, Customer Name, Phone, Email..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              sx={{ flexGrow: 1, minWidth: 240 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Order Status</InputLabel>
              <Select
                value={statusFilter}
                label="Order Status"
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="Processing">Processing</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            {/* Date Filters */}
            <TextField
              size="small"
              type="date"
              label="From Date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />

            <TextField
              size="small"
              type="date"
              label="To Date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />

            {(searchQuery || statusFilter !== "ALL" || startDate || endDate) && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<FilterAltOff />}
                onClick={clearFilters}
                sx={{ textTransform: "none", height: 40 }}
              >
                Clear
              </Button>
            )}
          </Paper>

          <Card elevation={0} sx={{ border: "1px solid #EAEAEA", borderRadius: 2 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead sx={{ bgcolor: "#F3F4F6" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Order #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <CircularProgress sx={{ color: "#1E40AF" }} />
                      </TableCell>
                    </TableRow>
                  ) : filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#666" }}>
                        No orders found matching your search or date criteria.
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell sx={{ fontWeight: 600, color: "#1E40AF" }}>
                          {order.order_number}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.customer_name}</Typography>
                          {order.customer_email && (
                            <Typography variant="caption" color="text.secondary">{order.customer_email}</Typography>
                          )}
                        </TableCell>
                        <TableCell>{order.customer_phone}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>₹{order.total_amount?.toLocaleString("en-IN")}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.payment_status}
                            size="small"
                            color={order.payment_status === "Paid" ? "success" : "warning"}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.order_status}
                            size="small"
                            color={getStatusChipColor(order.order_status)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem" }}>
                          {order.created_at ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#1F2937", fontSize: "0.85rem" }}>
                                {(() => {
                                  const raw = String(order.created_at).replace(/-/g, '/');
                                  const d = new Date(raw);
                                  return !isNaN(d.getTime())
                                    ? d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                    : String(order.created_at).split(' ')[0];
                                })()}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#6B7280", display: "block", fontSize: "0.75rem" }}>
                                {String(order.created_at).includes(' ') ? String(order.created_at).split(' ')[1] : ''}
                              </Typography>
                            </Box>
                          ) : "-"}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => handleOpenDetail(order)}
                            sx={{ mr: 1, color: "#1E40AF" }}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            startIcon={<Edit />}
                            color="secondary"
                            onClick={() => handleOpenStatus(order)}
                          >
                            Update
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredOrders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Card>
        </Box>
      </Box>

      {/* Order Detail Modal */}
      <Dialog open={openDetailDialog} onClose={() => setOpenDetailDialog(false)} maxWidth="md" fullWidth>
        {selectedOrder && (
          <>
            <DialogTitle sx={{ fontWeight: 700, color: "#1E40AF", borderBottom: "1px solid #EEE" }}>
              Order Details - {selectedOrder.order_number}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Customer Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedOrder.customer_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone / Email</Typography>
                  <Typography variant="body1">{selectedOrder.customer_phone} | {selectedOrder.customer_email || "N/A"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Shipping Address</Typography>
                  <Typography variant="body1">{selectedOrder.shipping_address}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Items Ordered</Typography>
              {selectedOrder.items && selectedOrder.items.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "#FAFAFA" }}>
                      <TableRow>
                        <TableCell>Product Name</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell align="right">₹{item.price}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>₹{item.total}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">No item breakdown available.</Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: "1px solid #EEE" }}>
              <Button onClick={() => setOpenDetailDialog(false)} variant="contained" sx={{ bgcolor: "#1E40AF" }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Status & Order Details Update Modal */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1E40AF", borderBottom: "1px solid #EEE" }}>
          Edit Order Details & Status — {selectedOrder?.order_number}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Order Status</InputLabel>
                <Select
                  value={newOrderStatus}
                  label="Order Status"
                  onChange={(e) => setNewOrderStatus(e.target.value)}
                >
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Processing">Processing</MenuItem>
                  <MenuItem value="Shipped">Shipped</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={newPaymentStatus}
                  label="Payment Status"
                  onChange={(e) => setNewPaymentStatus(e.target.value)}
                >
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Refunded">Refunded</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Customer Name"
                value={editCustomerName}
                onChange={(e) => setEditCustomerName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Customer Phone"
                value={editCustomerPhone}
                onChange={(e) => setEditCustomerPhone(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Customer Email"
                value={editCustomerEmail}
                onChange={(e) => setEditCustomerEmail(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                label="Shipping Address"
                value={editShippingAddress}
                onChange={(e) => setEditShippingAddress(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #EEE" }}>
          <Button onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={updating}
            sx={{ bgcolor: "#1E40AF" }}
          >
            {updating ? "Saving Changes..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderList;
